import { InfoCircleOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Tooltip,
  message,
} from 'antd';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { BsBox2, BsBoxSeam, BsCurrencyDollar, BsImages, BsRulers } from 'react-icons/bs';
import { Link, useHistory, useLocation } from 'react-router-dom';

import PPPageHeader from '../../components/pageHeader/PPPageHeader';
import useStore from '../../store/useStore';
import { commonRegExp, getProductSizeOptions, getProductWeightOptions } from '../../utils';
import { formatThousands, parseThousands } from '../../utils/numberInput';
import {
  convertProductSize,
  convertProductWeight,
  convertToCm,
  convertToGramm,
} from '../../utils/format';
import FormFooter from './components/FormFooter';
import SectionCard from './components/SectionCard';
import CustomUpload from './CustomUpload';
import CustomUploadMultiImage from './CustomUploadMultiImage';
import {
  PRODUCT_DESCRIPTION_MAX_LENGTH,
  PRODUCT_TITLE_MAX_LENGTH,
  nonNegativeRule,
  productDescriptionRules,
  productTitleRules,
  requiredText,
  trimTextFields,
  wholeNumberRule,
} from '../../utils/formRules';

const { TextArea } = Input;

/** Талбарууд картын бүтэн өргөнөөр сунахгүй — зүүн талд багцлагдана */
const FORM_MAX_WIDTH = 960;

/** Хадгалахын өмнө эхэн/төгсгөлийн илүү зайг цэвэрлэх текст талбарууд */
const TRIMMABLE_TEXT_FIELDS = ['title', 'description'];

/**
 * Тоон талбар. Утгыг ЗОРИУДААР засварладаггүй — өмнө нь `min` болон "-"-г хасах
 * `parser` нь буруу утгыг чимээгүй өөрчилдөг тул хэрэглэгч бичсэнээсээ өөр утга
 * хадгалагдсаныг мэдэхгүй үлддэг байсан. Шалгуурыг `validation.js`-ийн дүрмүүд
 * хийж, алдааг ил харуулна.
 */
const numberFieldProps = {
  style: { width: '100%' },
};

/** Талбарын тайлбарыг оролтын баруун захад дүрсээр харуулна */
const FieldHint = ({ title, description }) => (
  <Tooltip
    placement="bottomRight"
    overlayInnerStyle={{ backgroundColor: '#fff', color: '#000' }}
    title={
      <div style={{ padding: 8 }}>
        <h4 style={{ color: '#2FA1D9', fontWeight: 600 }}>{title}</h4>
        <p style={{ marginBottom: 0 }}>{description}</p>
      </div>
    }
  >
    <InfoCircleOutlined style={{ color: '#2FA1D9', cursor: 'pointer' }} />
  </Tooltip>
);

const Edit = () => {
  const location = useLocation();
  const history = useHistory();
  const isAutoStockLowProcurement = useStore((state) => state?.user?.isAutoStockLowProcurement);
  const productDetail = location?.state?.product ?? {};
  const merchantCode = productDetail.merchantCode;
  const isErlian = merchantCode === 'EHW';
  const [categoryList, setCategoryList] = useState([]);
  const [brandList, setBrandList] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [brandLoading, setBrandLoading] = useState(false);
  // Давхар дарахад засварын хүсэлт хоёр удаа үүсэхээс сэргийлнэ
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Алдааны мэдэгдлийг axios interceptor гаргана. Хариунд жагсаалт ирээгүй ч
    // `[]` хэвээр үлдээнэ — `undefined` болбол `options={list.map(...)}` унана.
    setCategoryLoading(true);
    axios
      .post('/api/v1.0/product/category')
      .then(({ data }) => {
        setCategoryList(data?.result?.docs ?? []);
      })
      .catch((e) => {
        console.error('Category list fetch error: ', e);
      })
      .finally(() => setCategoryLoading(false));

    setBrandLoading(true);
    axios
      .post('/api/v1.0/product/group')
      .then(({ data }) => {
        setBrandList(data?.result?.docs ?? []);
      })
      .catch((e) => {
        console.error('Brand list fetch error: ', e);
      })
      .finally(() => setBrandLoading(false));
  }, []);

  const onFinish = (values) => {
    // Бөглөсөн текстийн эхэн/төгсгөлийн илүү зай бараан дээр үлдэхээс сэргийлнэ
    Object.assign(values, trimTextFields(values, TRIMMABLE_TEXT_FIELDS));

    if (typeof values.imageCover?.[0] === 'object') {
      values.imageCover = values.imageCover?.[0]?.url;
    }

    if (typeof values.images?.[0] === 'object') {
      values.images = values.images?.map((item) => item?.url ?? '');
    }

    if (typeof values.size === 'object' && Object.keys(values.size).length > 0) {
      let volume = 1;
      //m to cm
      for (const key in values.size) {
        if (Object.hasOwnProperty.call(values.size, key)) {
          const value = values.size[key];
          if (typeof value === 'number') {
            let size = convertToCm(values.sizeMeasure, value);
            values.size[`${key}`] = size;
            volume *= size;
          }
        }
      }
      values.volume = volume;
      values.vVolume = volume / 1000;
    }

    values.weight = convertToGramm(values.weightMeasure, values.weight);

    setSubmitting(true);
    axios
      .post(
        `/api/v1.0/product/createUpdateRequest`,
        (() => {
          const payload = {
            ...values,
            productSku: values.productSku || productDetail.productSku,
            _id: productDetail._id,
          };
          return payload;
        })()
      )
      // .post(`/api/v1.0/product/${productDetail._id}`, values)
      .then(({ data }) => {
        if (data.statusCode === 200) {
          message.success('Хүсэлт амжилттай үүслээ.');
          history.push('/merchant/product/list');
        }
      })
      .catch((err) => {
        console.error('Product update request error: ', err);
      })
      .finally(() => setSubmitting(false));
  };

  const getInitialValues = () => {
    const detail = {
      ...(productDetail ?? {}),
      category: productDetail?.categoryItem?.code,
      isAttributes: Boolean(productDetail.attributes),
    };
    if (typeof detail.size === 'object' && Object.keys(detail.size).length > 0) {
      //m to cm
      for (const key in productDetail.size) {
        if (Object.hasOwnProperty.call(productDetail.size, key)) {
          let value = productDetail.size[key];
          if (typeof value === 'number') {
            detail.size[`${key}`] = convertProductSize(value, detail?.sizeMeasure);
          }
        }
      }
    }
    //kg to gramm
    if (detail.weight) {
      detail.weight = convertProductWeight(detail?.weight || 0, detail?.weightMeasure);
    }

    return detail;
  };

  const routes = [
    {
      to: {
        pathname: '/merchant/product/list',
        search: location.state?.search,
      },
      breadcrumbName: 'Бараа',
    },
    {
      path: `/merchant/products/${productDetail._id}`,
      breadcrumbName: 'Нэгж бараа',
    },
    {
      breadcrumbName: 'Засах',
    },
  ];

  return (
    <>
      <PPPageHeader
        title="Барааны мэдээлэл засах"
        breadcrumb={{ routes }}
        /** "Буцах" товчтой ижил — өмнөх дэлгэрэнгүй хуудас руу буцна */
        onBack={() =>
          history.push({
            pathname: `/merchant/products/${productDetail._id}`,
            state: { search: location.state?.search },
          })
        }
      />

      <Form
        name="basic"
        layout="vertical"
        initialValues={getInitialValues()}
        autoComplete="off"
        onFinish={onFinish}
      >
        <Space direction="vertical" size={16} style={{ width: '100%', padding: 16 }}>
          <SectionCard icon={<BsImages />} title="Барааны зураг">
            <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
              <Col>
                <Form.Item
                  label="Үндсэн зураг"
                  name="imageCover"
                  rules={[{ required: true, message: 'Үндсэн зураг оруулна уу!' }]}
                  style={{ marginBottom: 0 }}
                >
                  <CustomUpload />
                </Form.Item>
              </Col>
              <Col flex="auto">
                <Form.Item label="Нэмэлт зураг" name="images" style={{ marginBottom: 0 }}>
                  <CustomUploadMultiImage />
                </Form.Item>
              </Col>
            </Row>
          </SectionCard>

          <SectionCard icon={<BsBox2 />} title="Барааны мэдээлэл">
            <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item label="Барааны нэр" name="title" rules={productTitleRules()}>
                  {/* Хязгаарыг тоолуураар шууд харуулж, түүнээс цааш бичихийг хаана */}
                  <Input placeholder="Барааны нэр" maxLength={PRODUCT_TITLE_MAX_LENGTH} showCount />
                </Form.Item>
              </Col>

              {!isErlian && (
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item label="Брэнд" name="brand">
                    <Select
                      placeholder="Сонгох"
                      loading={brandLoading}
                      options={brandList.map((brand) => ({
                        value: brand?.name,
                        label: brand?.name,
                      }))}
                    />
                  </Form.Item>
                </Col>
              )}

              {!isErlian && (
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label="Категори"
                    name="category"
                    rules={[{ required: true, message: 'Категори оруулна уу!' }]}
                  >
                    <Select
                      placeholder="Сонгох"
                      loading={categoryLoading}
                      options={categoryList.map((category) => ({
                        value: category?.code,
                        label: category?.name,
                      }))}
                    />
                  </Form.Item>
                </Col>
              )}

              <Col xs={24} sm={12} lg={8}>
                <Form.Item label="SKU" name="productSku">
                  <Input placeholder="SKU" disabled />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label="Барааны тайлбар"
                  name="description"
                  rules={productDescriptionRules()}
                >
                  <TextArea
                    rows={3}
                    placeholder="Барааны тайлбар"
                    maxLength={PRODUCT_DESCRIPTION_MAX_LENGTH}
                    showCount
                  />
                </Form.Item>
              </Col>

              {!isErlian && (
                <>
                  <Col xs={24} sm={12} lg={8}>
                    <Form.Item
                      label="Буцаагддаг бараа эсэх?"
                      name="isReturnable"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <Form.Item
                      label="Дуусах хугацаатай эсэх?"
                      name="willExpire"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <Form.Item
                      label="Эвдрэх аюултай эсэх?"
                      name="isFragile"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                </>
              )}
            </Row>
          </SectionCard>

          <SectionCard icon={<BsRulers />} title="Овор хэмжээ">
            <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
              <Col xs={12} sm={12} lg={6}>
                <Form.Item
                  label="Нэгж"
                  name="weightMeasure"
                  rules={[{ required: true, message: 'Нэгж оруулна уу!' }]}
                >
                  <Select placeholder="Сонгох" options={getProductWeightOptions()} />
                </Form.Item>
              </Col>

              <Col xs={12} sm={12} lg={6}>
                <Form.Item
                  label="Жин"
                  name="weight"
                  /*
                   * Шалгуур нь бараа үүсгэх хуудастай ЯГ ижил байх ёстой. Өмнө
                   * нь энд нэмэлт `min` (0.1 кг / 100 гр) дүрмүүд байсан тул
                   * үүсгэх үедээ асуудалгүй өнгөрсөн хөнгөн бараа (ж: 1 гр =
                   * 0.001 кг) засах үедээ хадгалагдахаа больдог байв.
                   */
                  rules={[
                    {
                      pattern: new RegExp(commonRegExp.greaterThanZero),
                      message: 'Жин 0-ээс их байх ёстой!',
                    },
                    {
                      required: true,
                      message: 'Жин оруулна уу!',
                    },
                  ]}
                >
                  <InputNumber {...numberFieldProps} />
                </Form.Item>
              </Col>

              <Col xs={12} sm={12} lg={6}>
                <Form.Item
                  label="Урт"
                  name={['size', 'length']}
                  rules={[
                    {
                      pattern: new RegExp(commonRegExp.greaterThanZero),
                      message: 'Урт 0 - ээс их байх ёстой ',
                    },
                  ]}
                >
                  <InputNumber {...numberFieldProps} />
                </Form.Item>
              </Col>

              <Col xs={12} sm={12} lg={6}>
                <Form.Item
                  label="Өргөн"
                  name={['size', 'width']}
                  rules={[
                    {
                      pattern: new RegExp(commonRegExp.greaterThanZero),
                      message: 'Өргөн 0 - ээс их байх ёстой ',
                    },
                  ]}
                >
                  <InputNumber {...numberFieldProps} />
                </Form.Item>
              </Col>

              <Col xs={12} sm={12} lg={6}>
                <Form.Item
                  label="Өндөр"
                  name={['size', 'height']}
                  rules={[
                    {
                      pattern: new RegExp(commonRegExp.greaterThanZero),
                      message: 'Өндөр 0 - ээс их байх ёстой ',
                    },
                  ]}
                >
                  <InputNumber {...numberFieldProps} />
                </Form.Item>
              </Col>

              <Col xs={12} sm={12} lg={6}>
                <Form.Item
                  label="Нэгж"
                  name="sizeMeasure"
                  rules={[
                    {
                      required: false,
                      message: 'Эзэлхүүн хэмжих нэгж оруулна уу.',
                    },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <Select placeholder="Сонгох" options={getProductSizeOptions()} />
                </Form.Item>
              </Col>
            </Row>
          </SectionCard>

          <SectionCard icon={<BsBoxSeam />} title="Нөөцийн мэдээлэл">
            <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label="Зайлшгүй байх нөөц"
                  name="minStock"
                  rules={[
                    { required: true, message: 'Зайлшгүй байх нөөц' },
                    wholeNumberRule('Зайлшгүй байх нөөц'),
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber {...numberFieldProps} name="minStock" />
                </Form.Item>
              </Col>

              {isAutoStockLowProcurement && (
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label="Дахин захиалга хийх тоо хэмжээ"
                    name="reorderQuantity"
                    rules={[
                      { required: true, message: 'Дахин захиалга хийх тоо хэмжээ' },
                      wholeNumberRule('Дахин захиалга хийх тоо хэмжээ'),
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber {...numberFieldProps} name="reorderQuantity" />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </SectionCard>

          {!isErlian && (
            <SectionCard icon={<AiOutlineInfoCircle />} title="Бусад мэдээлэл">
              <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
                <Col xs={24}>
                  <Form.Item
                    name="isAttributes"
                    label="Өнгө, хэмжээ төрөл бүртгэх эсэх?"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>

                <Form.Item
                  noStyle
                  shouldUpdate={(prevVals, currentVals) =>
                    prevVals.isAttributes !== currentVals.isAttributes
                  }
                >
                  {({ getFieldValue }) =>
                    getFieldValue('isAttributes') ? (
                      <>
                        <Col xs={24} sm={12} lg={8}>
                          <Form.Item label="Өнгө" name={['attributes', 'color']}>
                            <Input placeholder="-" />
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} lg={8}>
                          <Form.Item label="Хэмжээ" name={['attributes', 'size']}>
                            <Input
                              placeholder="-"
                              suffix={
                                <FieldHint
                                  title="Хэмжээ гэж юу вэ?"
                                  description="Хэмжээ нь тухайн барааны шатлал буюу жижиг, дунд, том гэх мэт хэмжээсүүдийг хэлнэ."
                                />
                              }
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} lg={8}>
                          <Form.Item label="Төрөл" name={['attributes', 'type']}>
                            <Input
                              placeholder="-"
                              suffix={
                                <FieldHint
                                  title="Төрөл гэж юу вэ?"
                                  description="Төрөл нь тухайн барааны ямар төрөл ангилал болохыг хэлнэ."
                                />
                              }
                            />
                          </Form.Item>
                        </Col>
                      </>
                    ) : null
                  }
                </Form.Item>

                <Form.List name="barCodes">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Col key={key} xs={24} sm={12} lg={8}>
                          <Form.Item
                            {...restField}
                            label={`Код ${name + 1}`}
                            name={name}
                            rules={[requiredText('Код оруулна уу!')]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input
                              placeholder="Код оруулна уу"
                              suffix={
                                <MinusCircleOutlined
                                  onClick={() => remove(name)}
                                  style={{ color: '#8993a5', cursor: 'pointer' }}
                                />
                              }
                            />
                          </Form.Item>
                        </Col>
                      ))}

                      <Col xs={24} sm={12} lg={8}>
                        {/* Талбартай нэг эгнээнд байрлуулахын тулд хоосон label үлдээв */}
                        <Form.Item label={fields.length ? ' ' : 'Код'} style={{ marginBottom: 0 }}>
                          <Button onClick={() => add()} type="default" icon={<PlusOutlined />}>
                            Код нэмэх
                          </Button>
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Form.List>
              </Row>
            </SectionCard>
          )}

          <SectionCard icon={<BsCurrencyDollar />} title="Үнийн мэдээлэл">
            <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label="Зарах үнэ"
                  name="price"
                  rules={[
                    { required: true, message: 'Зарах үнэ оруулна уу!' },
                    nonNegativeRule('Зарах үнэ'),
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber
                    {...numberFieldProps}
                    formatter={formatThousands}
                    parser={parseThousands}
                  />
                </Form.Item>
              </Col>
            </Row>
          </SectionCard>

          <FormFooter>
            <Link
              to={{
                pathname: `/merchant/products/${productDetail._id}`,
                state: { search: location.state?.search },
              }}
            >
              <Button htmlType="button">Буцах</Button>
            </Link>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Хадгалах
            </Button>
          </FormFooter>
        </Space>
      </Form>
    </>
  );
};

export default Edit;
